import { createReducer, on } from '@ngrx/store';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ApplicationState } from './application.model';
import { authenticateLoginUserErrorAction, changePortalEntityAction, getPortalEntitiesSuccessAction, handleValidUserAction, logoutAction } from './application.actions';
import * as Enumerable from 'linq';
import { PortalEntityResponse, SecurityRoleTiny, UserType } from '@jms/model';
import { PORTAL_AUTH_TOKEN_KEY } from '@jms/utils/util.authentication';

export { PORTAL_AUTH_TOKEN_KEY };

export const auth_token = localStorage.getItem(PORTAL_AUTH_TOKEN_KEY);
const savedUser = auth_token ? new JwtHelperService({}).decodeToken(auth_token) : null;
const tenantRole = getPortalRole(savedUser);

const initialState = {
    navExpanded: true,
    token: auth_token,
    user: savedUser,
    settings: {
        applicationName: 'WorkBuddy',
        applicationDescription: 'Welcome to the WorkBuddy portal',
        logo: '',
        banner: '/images/banner.jpg'
    },
    route: null,
    returnUrl: null,
    domain: '',
    entities: [],
    selectedEntity: null,
    roles: [],
    activeRole: null,
    tenantRole: tenantRole,
    info: null,
    tenantSettings: [],
    selectedTenantSetting: null,
    maintenanceTenants: []
} as ApplicationState;


const applicationReducer = createReducer(
    initialState,
    on(handleValidUserAction, (state: ApplicationState, action: any) => {

        const { token, returnUrl, maintenanceTenants } = action.response;

        localStorage.setItem(PORTAL_AUTH_TOKEN_KEY, token ?? '');
        
        // Clear previous user's entity selection on new login
        localStorage.removeItem('selectedEntityId');

        const jwt = new JwtHelperService({});
        const user = token ? jwt.decodeToken(token) : null;

        const role = getPortalRole(user);

        return {
            ...state,
            token: token,
            domain: user ? user.domain : null,
            user: user,
            login: null,
            navExpanded: false,
            tenantRole: role,
            returnUrl: returnUrl,
            maintenanceTenants: maintenanceTenants ?? []
        };
    }),
    on(getPortalEntitiesSuccessAction, (state: ApplicationState, action: any) => {

        const entities = Enumerable.from(action.response as PortalEntityResponse[])
            .selectMany(x => x.entities)
            .toArray();

        // Check local storage for previously selected entity
        const savedEntityId = localStorage.getItem('selectedEntityId');
        let selectedEntity = savedEntityId 
            ? Enumerable.from(entities).firstOrDefault(e => e._id === savedEntityId)
            : null;
        
        // If no saved entity or saved entity not found in current entities, use first
        if (!selectedEntity) {
            selectedEntity = Enumerable.from(entities).firstOrDefault();
            // Save the default selection to localStorage for consistency
            if (selectedEntity?._id) {
                localStorage.setItem('selectedEntityId', selectedEntity._id);
            }
        }
        
        const activeRole = selectedEntity.role;
        const roles = Enumerable.from(entities)
            .select(x => x.role)
            .distinct(x => x.name)
            .toArray();

        const tenantSettings = Enumerable.from(action.response as PortalEntityResponse[])
            .select(x => x.tenantSetting)
            .toArray();

        const selectedTenantSetting = Enumerable.from(tenantSettings).firstOrDefault(x => x.tenantName === selectedEntity.tenantName);

        return {
            ...state,
            entities: entities,
            selectedEntity: selectedEntity,
            activeRole: activeRole,
            roles: roles,
            tenantSettings: tenantSettings,
            selectedTenantSetting: selectedTenantSetting
        };
    }),
    on(logoutAction, (state: ApplicationState, action: any) => {

        localStorage.removeItem(PORTAL_AUTH_TOKEN_KEY);
        localStorage.removeItem('selectedEntityId');
        return { ...state, token: null };
    }),
    on(authenticateLoginUserErrorAction, (state: ApplicationState, action: any) => {
        localStorage.removeItem(PORTAL_AUTH_TOKEN_KEY);
        return { ...state, token: null, user: null };
    }),
    on(changePortalEntityAction, (state: ApplicationState, action: any) => {
        const selectedTenantSetting = Enumerable.from(state.tenantSettings).firstOrDefault(x => x.tenantName === action.entity.tenantName);
        // Save selected entity to local storage for persistence across tabs
        if (action.entity?._id) {
            localStorage.setItem('selectedEntityId', action.entity._id);
        }
        // Recalculate active role on parent switch so portal permissions (e.g. Request New Job/Quote) reflect the newly selected parent
        return { ...state, selectedEntity: action.entity, activeRole: action.entity?.role, selectedTenantSetting: selectedTenantSetting };
    })
);

export function reducer(state, action) {
    return applicationReducer(state, action);
}

function getPortalRole(user: any): SecurityRoleTiny | null {

    const tenantRole = Enumerable.from((user?.tenants ?? []) as any[])
        .select(x => x?.role as SecurityRoleTiny)
        .where(x => !!x && x.type !== UserType.Employee)
        .firstOrDefault();

    return tenantRole ?? (user?.role?.type !== UserType.Employee ? user?.role : null);
}
